import {createContext, Dispatch, FC, ReactNode, Reducer, useContext, useEffect} from "react";
import {useReducerAsync} from "use-reducer-async";
import AssignmentReducer, {
  asyncActionHandlers,
  AssignmentActions,
  AssignmentAsyncActions,
  AssignmentState as AssignmentState
} from "./AssignmentReducer";
import {Constants} from "../constants/constants";
import log from "loglevel";

const InitialAssignmentState: AssignmentState = {
  originManifest: undefined,
  selectedJobs: [],
  selectedStops: [],
  pendingActions: [],
  selectedManifest: undefined,
  highlightManifests: [],
  jobsRequirements: new Map(),
  reassignJobsRequirements: new Map(),
  splitJobDriver: undefined
};

export const AssignmentStateContext = createContext<AssignmentState | undefined>(undefined);
export const AssignmentDispatchContext = createContext<
  Dispatch<AssignmentActions | AssignmentAsyncActions> | undefined
>(undefined);

export const AssignmentProvider: FC<{children: ReactNode}> = ({children}) => {
  const [assignmentState, assignmentStateDispatch] = useReducerAsync<
    Reducer<AssignmentState, AssignmentActions>,
    AssignmentAsyncActions
  >(AssignmentReducer, InitialAssignmentState, asyncActionHandlers);

  log.debug("pendingActions", assignmentState.pendingActions);

  const hasPendingActions = assignmentState.pendingActions.length > 0;

  useEffect(() => {
    if (!hasPendingActions) return;
    const interval = setInterval(() => {
      assignmentStateDispatch({type: "CleanStalePendingActions"});
    }, Constants.GENERAL_POLLING_INTERVAL * 2);
    return () => clearInterval(interval);
  }, [hasPendingActions, assignmentStateDispatch]);

  return (
    <AssignmentStateContext.Provider value={assignmentState}>
      <AssignmentDispatchContext.Provider value={assignmentStateDispatch}>
        {children}
      </AssignmentDispatchContext.Provider>
    </AssignmentStateContext.Provider>
  );
};

export const useAssignmentState = () => {
  const context = useContext(AssignmentStateContext);
  if (!context) {
    throw new Error("useAssignmentState must be used within a AssignmentProvider");
  }
  return context;
};

export const useAssignmentDispatch = () => {
  const context = useContext(AssignmentDispatchContext);
  if (!context) {
    throw new Error("useAssignmentDispatch must be used within a AssignmentProvider");
  }
  return context;
};
